import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { MoneyInput } from './MoneyInput';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Trash2, Plus, Upload, Paperclip, Check, AlertTriangle, Pin, History, Info } from 'lucide-react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { SearchableSelect } from './SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = ['FA A', 'FA B', 'FA C', 'FA M', 'NC A', 'NC B', 'NC C', 'ND A', 'ND B', 'ND C', 'Ticket', 'Recibo', 'Otro'];
const IVA_RATES = [21, 10.5, 27, 5, 2.5, 0];

export const emptyComprobante = () => ({
  supplier_id: '', supplier_business_name: '', supplier_tax_id: '', supplier_tax_condition: '',
  expense_concept_id: '',
  imputation_type: 'account', imputation_account_code: '',
  fecha_comprobante: new Date().toISOString().slice(0, 10),
  tipo_comprobante: 'FA A', punto_venta: '', numero: '',
  items_iva: [{ base_neta: '', alicuota: 21 }],
  no_gravado: '', exento: '',
  percepciones: [],
  attachment_id: '', _filename: '',
  // v3.38.0 — Guardar imputación como default del proveedor (SOLO Otras Compras)
  save_as_default: false,
});

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
// Formato ARCA 00000-00000000: completa con ceros a la izquierda al salir del campo
const padArca = (v, len) => {
  const digits = String(v || '').replace(/\D/g, '').slice(0, len);
  return digits ? digits.padStart(len, '0') : '';
};
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function comprobanteTotals(c) {
  const neto = (c.items_iva || []).reduce((s, it) => s + (parseFloat(it.base_neta) || 0), 0);
  const iva = (c.items_iva || []).reduce((s, it) => s + round2((parseFloat(it.base_neta) || 0) * (parseFloat(it.alicuota) || 0) / 100), 0);
  const perc = (c.percepciones || []).reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const exento = (parseFloat(c.exento) || 0) + (parseFloat(c.no_gravado) || 0);
  return { neto: round2(neto), iva: round2(iva), perc: round2(perc), exento: round2(exento), total: round2(neto + iva + perc + exento) };
}

export default function PurchaseInvoiceForm({ value, onChange, onRemove, suppliers = [], expenseConcepts = [], accounts = [], withholdingTypes = [], index = 0, showConcept = true, imputationMode = false, imputationSuggestionMode = false }) {
  const [dupInfo, setDupInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);
  // v3.38.0 — Sugerencia de imputación por proveedor
  const [suggestion, setSuggestion] = useState(null); // {source, imputation_type, ..., usage_profile}
  const lastFetchedSupplier = useRef(null);
  const c = value;
  const set = (patch) => onChange({ ...c, ...patch });
  const t = comprobanteTotals(c);

  const checkDuplicate = async (numero) => {
    const supplierId = c.supplier_id;
    const taxId = c.supplier_tax_id;
    if (!numero || (!supplierId && !taxId)) return;
    setChecking(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/purchase-invoices/check-duplicate`, {
        params: {
          numero, punto_venta: c.punto_venta || '', tipo_comprobante: c.tipo_comprobante || '',
          supplier_id: supplierId || undefined, supplier_tax_id: supplierId ? undefined : taxId,
        }, withCredentials: true,
      });
      if (r.data?.duplicate) setDupInfo(r.data.invoice);
    } catch { /* el backend igual bloquea al guardar */ }
    finally { setChecking(false); }
  };

  const clearDuplicate = () => {
    setDupInfo(null);
    set({ numero: '' });
  };

  const onSupplier = (sid) => {
    const s = suppliers.find(x => x.supplier_id === sid);
    set({
      supplier_id: sid,
      supplier_business_name: s?.business_name || '',
      supplier_tax_id: s?.tax_id || '',
      supplier_tax_condition: s?.tax_condition || '',
      // Reseteamos el flag para que no quede tildado de un proveedor anterior
      save_as_default: false,
    });
  };

  // v3.38.0 — Al cambiar proveedor en modo imputación, traer sugerencia y precargar
  useEffect(() => {
    if (!imputationSuggestionMode || !imputationMode) return;
    const sid = c.supplier_id;
    if (!sid) { setSuggestion(null); lastFetchedSupplier.current = null; return; }
    if (lastFetchedSupplier.current === sid) return; // ya se trajo
    lastFetchedSupplier.current = sid;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/suppliers/${sid}/imputation-suggestion`, { withCredentials: true });
        const sug = r.data || null;
        setSuggestion(sug);
        // Autocompletar SOLO si el usuario todavía no eligió imputación
        if (sug && sug.source !== 'none' && !c.imputation_account_code && !c.expense_concept_id) {
          if (sug.imputation_type === 'account' && sug.imputation_account_code) {
            set({ imputation_type: 'account', imputation_account_code: sug.imputation_account_code, expense_concept_id: '' });
          } else if (sug.imputation_type === 'concept' && sug.expense_concept_id) {
            set({ imputation_type: 'concept', expense_concept_id: sug.expense_concept_id, imputation_account_code: '' });
          }
        }
      } catch {
        setSuggestion(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.supplier_id, imputationSuggestionMode, imputationMode]);

  // Lógica del checkbox "Guardar como predeterminada":
  // - Oculto si la cuenta/concepto elegido coincide con el default actual del proveedor
  // - Tildado por defecto si el proveedor NO tiene default y el usuario eligió algo
  // - Destildado por defecto si tiene uno distinto (cambio consciente)
  const saveAsDefaultState = (() => {
    if (!imputationSuggestionMode || !imputationMode || !c.supplier_id) return { show: false };
    const chosenAccount = c.imputation_type === 'account' ? c.imputation_account_code : null;
    const chosenConcept = c.imputation_type === 'concept' ? c.expense_concept_id : null;
    if (!chosenAccount && !chosenConcept) return { show: false };
    const defType = suggestion?.source === 'default' ? suggestion?.imputation_type : null;
    const defAcc = suggestion?.source === 'default' ? suggestion?.imputation_account_code : null;
    const defCon = suggestion?.source === 'default' ? suggestion?.expense_concept_id : null;
    // ¿Elegido = default actual? → ocultar
    const sameAsDefault = (defType === 'account' && defAcc && defAcc === chosenAccount)
                       || (defType === 'concept' && defCon && defCon === chosenConcept);
    if (sameAsDefault) return { show: false };
    const hasDefault = !!defType;
    return { show: true, suggestTick: !hasDefault, hasDefault };
  })();

  // Sincroniza el default sugerido (tildado automático) la primera vez que se cumplen condiciones
  const prevDefaultHintRef = useRef(null);
  useEffect(() => {
    if (!saveAsDefaultState.show) {
      if (c.save_as_default) set({ save_as_default: false });
      return;
    }
    const hint = `${c.supplier_id}|${c.imputation_type}|${c.imputation_account_code}|${c.expense_concept_id}`;
    if (prevDefaultHintRef.current === hint) return;
    prevDefaultHintRef.current = hint;
    // Solo aplica el default inteligente la primera vez para esta combinación
    set({ save_as_default: !!saveAsDefaultState.suggestTick });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveAsDefaultState.show, saveAsDefaultState.suggestTick, c.supplier_id, c.imputation_type, c.imputation_account_code, c.expense_concept_id]);

  const setItem = (i, patch) => set({ items_iva: c.items_iva.map((it, j) => j === i ? { ...it, ...patch } : it) });
  const addItem = () => set({ items_iva: [...c.items_iva, { base_neta: '', alicuota: 21 }] });
  const rmItem = (i) => set({ items_iva: c.items_iva.filter((_, j) => j !== i) });

  const setPerc = (i, patch) => set({ percepciones: c.percepciones.map((p, j) => j === i ? { ...p, ...patch } : p) });
  const addPerc = () => set({ percepciones: [...c.percepciones, { withholding_type_id: '', tipo: '', base: '', alicuota: '', monto: '' }] });
  const rmPerc = (i) => set({ percepciones: c.percepciones.filter((_, j) => j !== i) });

  const onPercType = (i, whtId) => {
    const w = withholdingTypes.find(x => x.withholding_type_id === whtId);
    setPerc(i, { withholding_type_id: whtId, tipo: w?.code || '' });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/purchase-invoices/image`, fd, { withCredentials: true });
      set({ attachment_id: r.data.attachment_id, _filename: r.data.original_filename });
      toast.success('Comprobante adjuntado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el comprobante');
    } finally { setUploading(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3" data-testid={`comprobante-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-amber-800">Comprobante #{index + 1}</span>
        {onRemove && <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={onRemove} data-testid={`comprobante-remove-${index}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>

      {/* Proveedor */}
      <div>
        <Label className="text-xs">Proveedor</Label>
        <SearchableSelect
          value={c.supplier_id}
          onChange={onSupplier}
          options={suppliers.map(s => ({ value: s.supplier_id, label: s.business_name, hint: s.tax_id ? `CUIT ${s.tax_id}` : undefined }))}
          placeholder="Buscar proveedor por nombre o CUIT..."
          emptyLabel="Ningún proveedor coincide"
          testId={`comprobante-supplier-${index}`}
        />
        {c.supplier_tax_id && <p className="text-[11px] text-muted-foreground mt-1">CUIT {c.supplier_tax_id} · {c.supplier_tax_condition || 's/cond.'}</p>}
      </div>

      {/* Imputación contable */}
      {imputationMode ? (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-2 space-y-2" data-testid={`comprobante-imputation-block-${index}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-xs font-semibold">Imputación contable *</Label>
            {imputationSuggestionMode && suggestion && suggestion.source !== 'none' && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex items-center gap-1 ${
                  suggestion.source === 'default'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-slate-50 border-slate-300 text-slate-700'
                }`}
                data-testid={`comprobante-suggestion-badge-${index}`}
                title={suggestion.source === 'default'
                  ? `Default configurado${suggestion.default_imputation_updated_by_name ? ' por ' + suggestion.default_imputation_updated_by_name : ''}`
                  : 'Última cuenta usada con este proveedor'}
              >
                {suggestion.source === 'default'
                  ? (<><Pin className="w-3 h-3" /> Sugerida por proveedor</>)
                  : (<><History className="w-3 h-3" /> Último uso</>)}
              </span>
            )}
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 text-sm">
              <input type="radio" name={`imp-${index}`} checked={c.imputation_type === 'account'} onChange={() => set({ imputation_type: 'account', expense_concept_id: '' })} data-testid={`comprobante-imp-account-${index}`} />
              Cuenta contable
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="radio" name={`imp-${index}`} checked={c.imputation_type === 'concept'} onChange={() => set({ imputation_type: 'concept', imputation_account_code: '' })} data-testid={`comprobante-imp-concept-${index}`} />
              Concepto de gasto
            </label>
          </div>
          {c.imputation_type === 'concept' ? (
            <select value={c.expense_concept_id} onChange={(e) => set({ expense_concept_id: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`comprobante-concept-${index}`}>
              <option value="">Seleccionar concepto…</option>
              {expenseConcepts.map(ec => <option key={ec.concept_id} value={ec.concept_id}>{ec.name}</option>)}
            </select>
          ) : (
            <select value={c.imputation_account_code} onChange={(e) => set({ imputation_account_code: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`comprobante-account-${index}`}>
              <option value="">Seleccionar cuenta…</option>
              {accounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
            </select>
          )}
          {saveAsDefaultState.show && (
            <label className="flex items-start gap-1.5 text-xs text-indigo-800" data-testid={`comprobante-save-default-wrap-${index}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!c.save_as_default}
                onChange={(e) => set({ save_as_default: e.target.checked })}
                data-testid={`comprobante-save-default-${index}`}
              />
              <span>
                <Pin className="inline w-3 h-3 mr-0.5" />
                Guardar esta imputación como <b>predeterminada</b> para este proveedor
                {saveAsDefaultState.hasDefault && (
                  <span className="text-amber-700 ml-1">(reemplaza la actual)</span>
                )}
              </span>
            </label>
          )}
          {imputationSuggestionMode && suggestion?.usage_profile?.is_mostly_stock && (
            <div className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1" data-testid={`comprobante-stock-warning-${index}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Este proveedor habitualmente opera por <b>recepción de stock</b>
                {' '}({suggestion.usage_profile.receptions} recepciones · {suggestion.usage_profile.other_purchases} otras compras).
                Confirmá que esta compra no es mercadería a inventariar.
              </span>
            </div>
          )}
        </div>
      ) : showConcept && (
        <div>
          <Label className="text-xs">Concepto de Gasto (imputación) *</Label>
          <select value={c.expense_concept_id} onChange={(e) => set({ expense_concept_id: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`comprobante-concept-${index}`}>
            <option value="">Seleccionar concepto…</option>
            {expenseConcepts.map(ec => <option key={ec.concept_id} value={ec.concept_id}>{ec.name}</option>)}
          </select>
        </div>
      )}

      {dupInfo && (
        <AlertDialog open onOpenChange={(o) => { if (!o) clearDuplicate(); }}>
          <AlertDialogContent data-testid={`duplicate-invoice-dialog-${index}`}>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                <AlertTriangle className="w-5 h-5" /> Comprobante ya cargado
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>
                    El comprobante <b className="font-mono">{dupInfo.comprobante}</b> de{' '}
                    <b>{dupInfo.supplier_name}</b> ya está registrado en el sistema.
                    No se puede cargar dos veces el mismo comprobante fiscal.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-2 text-xs space-y-0.5">
                    <div>Fecha del comprobante: <b>{(dupInfo.fecha_comprobante || '').slice(0, 10).split('-').reverse().join('/')}</b></div>
                    <div>Importe: <b>$ {Number(dupInfo.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</b></div>
                    {dupInfo.source_number && <div>Origen: <b>{dupInfo.source_number}</b></div>}
                    <div>Cargado el <b>{dupInfo.loaded_on}</b> por <b>{dupInfo.created_by_name}</b></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Si es un comprobante distinto, revisá el tipo, el punto de venta y el número.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={clearDuplicate} data-testid={`duplicate-invoice-ok-${index}`}>
                Corregir el número
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Datos del comprobante */}
      <div className="grid grid-cols-4 gap-2">
        <div className="col-span-1">
          <Label className="text-xs">Tipo</Label>
          <select value={c.tipo_comprobante} onChange={(e) => set({ tipo_comprobante: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`comprobante-type-${index}`}>
            {DOC_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Pto. Vta.</Label>
          <Input className="h-9 font-mono" value={c.punto_venta}
            onChange={(e) => set({ punto_venta: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            onBlur={(e) => set({ punto_venta: padArca(e.target.value, 5) })}
            placeholder="00001" data-testid={`comprobante-pv-${index}`} /></div>
        <div><Label className="text-xs">Número</Label>
          <Input className={`h-9 font-mono ${dupInfo ? 'border-rose-500 focus-visible:ring-rose-500' : ''}`} value={c.numero}
            onChange={(e) => { setDupInfo(null); set({ numero: e.target.value.replace(/\D/g, '').slice(0, 8) }); }}
            onBlur={(e) => { const n = padArca(e.target.value, 8); set({ numero: n }); checkDuplicate(n); }}
            placeholder="00001234" data-testid={`comprobante-num-${index}`} />
          {checking && <p className="text-[10px] text-muted-foreground mt-0.5">Verificando…</p>}</div>
        <div><Label className="text-xs">Fecha</Label><Input type="date" max={new Date().toISOString().slice(0,10)} className="h-9" value={c.fecha_comprobante} onChange={(e) => set({ fecha_comprobante: e.target.value })} data-testid={`comprobante-date-${index}`} /></div>
      </div>

      {/* Items IVA (multi-alícuota) */}
      <div className="space-y-1">
        <Label className="text-xs">Neto gravado por alícuota</Label>
        {c.items_iva.map((it, i) => {
          const iva = round2((parseFloat(it.base_neta) || 0) * (parseFloat(it.alicuota) || 0) / 100);
          return (
            <div key={i} className="flex gap-2 items-center">
              <MoneyInput className="h-8 flex-1" placeholder="$ 0,00" value={it.base_neta} onChange={(e) => setItem(i, { base_neta: e.target.value })} data-testid={`comprobante-${index}-neto-${i}`} />
              <select value={it.alicuota} onChange={(e) => setItem(i, { alicuota: parseFloat(e.target.value) })} className="flex h-8 w-24 rounded-md border border-input bg-background px-1 text-sm" data-testid={`comprobante-${index}-alic-${i}`}>
                {IVA_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
              </select>
              <span className="w-28 text-right text-xs text-muted-foreground">IVA {fmtMoney(iva)}</span>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmItem(i)} disabled={c.items_iva.length <= 1}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          );
        })}
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addItem} data-testid={`comprobante-add-item-${index}`}><Plus className="w-3 h-3 mr-1" />Alícuota</Button>
      </div>

      {/* Exento / No gravado */}
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Exento</Label><MoneyInput className="h-8" placeholder="$ 0,00" value={c.exento} onChange={(e) => set({ exento: e.target.value })} data-testid={`comprobante-exento-${index}`} /></div>
        <div><Label className="text-xs">No gravado</Label><MoneyInput allowNegative className="h-8" placeholder="$ 0,00" value={c.no_gravado} onChange={(e) => set({ no_gravado: e.target.value })} data-testid={`comprobante-nograv-${index}`} /></div>
      </div>

      {/* Percepciones */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Percepciones / Retenciones</Label>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addPerc} data-testid={`comprobante-add-perc-${index}`}><Plus className="w-3 h-3 mr-1" />Agregar</Button>
        </div>
        {c.percepciones.map((p, i) => (
          <div key={i} className="flex gap-2 items-center">
            <select value={p.withholding_type_id || ''} onChange={(e) => onPercType(i, e.target.value)} className="flex h-8 w-44 rounded-md border border-input bg-background px-1 text-sm" data-testid={`comprobante-${index}-perc-type-${i}`}>
              <option value="">Tipo…</option>
              {withholdingTypes.map(w => <option key={w.withholding_type_id} value={w.withholding_type_id}>{w.code} · {w.name}</option>)}
            </select>
            <MoneyInput className="h-8 flex-1" placeholder="$ 0,00" value={p.monto} onChange={(e) => setPerc(i, { monto: e.target.value })} data-testid={`comprobante-${index}-perc-${i}`} />
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmPerc(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        {withholdingTypes.length === 0 && c.percepciones.length > 0 && (
          <p className="text-[11px] text-amber-700">No hay retenciones/percepciones en el catálogo. Cargalas en Tesorería → Configuración → Retenciones.</p>
        )}
      </div>

      {/* Imagen + Total */}
      <div className="flex items-center justify-between pt-1 border-t border-amber-200">
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-amber-800 hover:underline">
          {c.attachment_id ? <Check className="w-4 h-4 text-emerald-600" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Subiendo…' : c.attachment_id ? (c._filename || 'Adjunto cargado') : 'Adjuntar imagen/PDF'}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid={`comprobante-file-${index}`} />
        </label>
        <span className="text-sm font-semibold" data-testid={`comprobante-total-${index}`}>Total {fmtMoney(t.total)}</span>
      </div>
    </div>
  );
}
